import React, { useState, useEffect, useCallback } from 'react'
import { opcionesDeporte } from '../../../lib/deportesOrg'
import { FileBarChart } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Card, CardContent, Input, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../ui'
import { getReporteRango, getTemporadas, getOrgDeportes } from '../../../lib/supabase'
import { useOrganization } from '../../../contexts/OrganizationContext'

const PERSONALIZADO = '__personalizado__'
const TODOS = '__todos__'
const SIN_DEPORTE = '__general__'

const fmt = (n) => Number(n || 0).toLocaleString('es-MX')

const InventarioReporte = () => {
  const { org } = useOrganization()
  const [temporadas, setTemporadas] = useState([])
  const [deportes, setDeportes] = useState([])
  const [temporadaId, setTemporadaId] = useState(PERSONALIZADO)
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [deporteId, setDeporteId] = useState(TODOS)
  const [filas, setFilas] = useState([])
  const [generado, setGenerado] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rangoError, setRangoError] = useState(null)

  const loadOpciones = useCallback(async () => {
    if (!org?.id) return
    const [temps, deps] = await Promise.all([
      getTemporadas(false),
      getOrgDeportes(org.id),
    ])
    setTemporadas(temps.data || [])
    setDeportes(deps.data || [])
  }, [org?.id])

  useEffect(() => { loadOpciones() }, [loadOpciones])

  const handleTemporadaChange = (value) => {
    setTemporadaId(value)
    if (value === PERSONALIZADO) return
    const t = temporadas.find((x) => x.id === value)
    if (t) {
      setDesde(t.fecha_inicio || '')
      setHasta(t.fecha_fin || '')
    }
  }

  const handleDesdeChange = (e) => {
    setDesde(e.target.value)
    setTemporadaId(PERSONALIZADO)
  }

  const handleHastaChange = (e) => {
    setHasta(e.target.value)
    setTemporadaId(PERSONALIZADO)
  }

  const handleGenerar = async () => {
    if (!desde || !hasta) {
      setRangoError('Selecciona el rango de fechas (Desde y Hasta) o elige una temporada.')
      return
    }
    if (desde > hasta) {
      setRangoError('La fecha "Desde" no puede ser posterior a "Hasta".')
      return
    }
    setRangoError(null)
    setLoading(true)
    const { data } = await getReporteRango({
      desde,
      hasta,
      deporteId: deporteId === TODOS ? null : deporteId,
    })
    setFilas(data || [])
    setGenerado(true)
    setLoading(false)
  }

  const subtitulo = (f) => [f.talla, f.modelo].filter(Boolean).join(' · ')

  return (
    <FeaturedLayout
      title="Reporte de inventario"
      subtitle="Movimientos por rango o temporada"
    >
      <Card className="mb-4">
        <CardContent className="py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="temporada">Temporada</Label>
              <Select value={temporadaId} onValueChange={handleTemporadaChange}>
                <SelectTrigger id="temporada">
                  <SelectValue placeholder="Personalizado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PERSONALIZADO}>Personalizado</SelectItem>
                  {temporadas.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="desde">Desde</Label>
              <Input id="desde" type="date" value={desde} onChange={handleDesdeChange} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hasta">Hasta</Label>
              <Input id="hasta" type="date" value={hasta} onChange={handleHastaChange} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="deporte">Deporte</Label>
              <Select value={deporteId} onValueChange={setDeporteId}>
                <SelectTrigger id="deporte">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todos</SelectItem>
                  <SelectItem value={SIN_DEPORTE}>General (sin deporte)</SelectItem>
                  {opcionesDeporte(deportes).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-end gap-3">
            {rangoError && (
              <span className="text-sm text-destructive">{rangoError}</span>
            )}
            <Button onClick={handleGenerar} disabled={loading}>
              {loading ? 'Generando...' : 'Generar'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {!generado ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileBarChart className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">
              Elige un rango o temporada y presiona Generar para ver el reporte.
            </p>
          </CardContent>
        </Card>
      ) : filas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileBarChart className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">
              No hay movimientos en el rango seleccionado.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          {/* Movil: el kardex de 7 columnas no cabe. Se cuenta la historia -- de cuanto
              partio y en cuanto acabo -- y solo se listan los movimientos que
              ocurrieron; los que estan en cero no aportan nada. */}
          <div className="sm:hidden divide-y px-4">
            {filas.map((f) => {
              const movs = [
                f.agregados ? `+${fmt(f.agregados)} agregados` : null,
                f.perdidos ? `−${fmt(f.perdidos)} perdidos` : null,
                f.desechados ? `−${fmt(f.desechados)} desechados` : null,
                f.ajustes ? `${f.ajustes > 0 ? '+' : ''}${fmt(f.ajustes)} ajustes` : null,
              ].filter(Boolean)
              return (
                <div key={f.id} className="flex items-start justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{f.nombre}</p>
                    {subtitulo(f) && (
                      <p className="text-xs text-muted-foreground truncate">{subtitulo(f)}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Inicial <span className="tabular-nums">{fmt(f.inicial)}</span>
                      {movs.length > 0 && ` · ${movs.join(' · ')}`}
                      {movs.length === 0 && ' · sin movimientos'}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold tabular-nums">{fmt(f.final)}</p>
                    <p className="text-[11px] text-muted-foreground">Final</p>
                  </div>
                </div>
              )
            })}
          </div>

          <Table className="hidden sm:table">
            <TableHeader>
              <TableRow>
                <TableHead>Artículo</TableHead>
                <TableHead className="text-right">Inicial</TableHead>
                <TableHead className="text-right">+Agregados</TableHead>
                <TableHead className="text-right">−Perdidos</TableHead>
                <TableHead className="text-right">−Desechados</TableHead>
                <TableHead className="text-right">±Ajustes</TableHead>
                <TableHead className="text-right">Final</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    <span className="font-medium">{f.nombre}</span>
                    {subtitulo(f) && (
                      <span className="block text-xs text-muted-foreground">{subtitulo(f)}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(f.inicial)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(f.agregados)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(f.perdidos)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(f.desechados)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(f.ajustes)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmt(f.final)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </FeaturedLayout>
  )
}

export default InventarioReporte
